/* ============================================================
   SMA — Shop UI
   1. Card image slider  — chevrons on desktop, swipe + dots on mobile
   2. Model-photo toggle — jumps every card / the PDP gallery to the
                           first image whose ALT text matches a keyword
   3. Collection tabs    — show one pre-rendered product grid at a time
   ============================================================ */
(function () {
  'use strict';

  if (window.smaShopUiLoaded) return;
  window.smaShopUiLoaded = true;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function keywordsOf(el) {
    var raw = (el && el.getAttribute('data-sma-keywords')) || '';
    return raw
      .toLowerCase()
      .split('|')
      .map(function (k) {
        return k.trim();
      })
      .filter(Boolean);
  }

  function altMatches(alt, keywords) {
    var value = (alt || '').toLowerCase();
    if (!value) return false;
    for (var i = 0; i < keywords.length; i++) {
      if (value.indexOf(keywords[i]) !== -1) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* 1. Card image slider                                                */
  /* ------------------------------------------------------------------ */

  function sliderParts(root) {
    return {
      track: root.querySelector('.sma-cslider__track'),
      slides: toArray(root.querySelectorAll('.sma-cslider__slide')),
      dots: toArray(root.querySelectorAll('.sma-cslider__dot')),
      prev: root.querySelector('.sma-cslider__arrow--prev'),
      next: root.querySelector('.sma-cslider__arrow--next'),
    };
  }

  function currentIndex(parts) {
    if (!parts.track || !parts.track.clientWidth) return 0;
    return Math.round(parts.track.scrollLeft / parts.track.clientWidth);
  }

  function syncSlider(root) {
    var parts = sliderParts(root);
    if (!parts.track) return;
    var index = currentIndex(parts);
    var last = parts.slides.length - 1;

    parts.dots.forEach(function (dot, i) {
      dot.classList.toggle('sma-cslider__dot--active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    if (parts.prev) parts.prev.disabled = index <= 0;
    if (parts.next) parts.next.disabled = index >= last;
  }

  function goToSlide(root, index, behavior) {
    var parts = sliderParts(root);
    if (!parts.track || !parts.slides.length) return;
    var max = parts.slides.length - 1;
    var target = Math.max(0, Math.min(max, index));
    var left = target * parts.track.clientWidth;

    if (typeof parts.track.scrollTo === 'function') {
      parts.track.scrollTo({ left: left, behavior: behavior || 'smooth' });
    } else {
      parts.track.scrollLeft = left;
    }
    // Snap-scrolling fires scroll events, but sync immediately for instant jumps.
    window.setTimeout(function () {
      syncSlider(root);
    }, 60);
  }

  function initSlider(root) {
    if (root.dataset.smaCsliderInit === 'true') return;
    root.dataset.smaCsliderInit = 'true';

    var parts = sliderParts(root);
    if (!parts.track) return;

    if (parts.slides.length < 2) {
      root.classList.add('sma-cslider--single');
      return;
    }

    var raf = null;
    parts.track.addEventListener(
      'scroll',
      function () {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = null;
          syncSlider(root);
        });
      },
      { passive: true }
    );

    syncSlider(root);
  }

  function initAllSliders(scope) {
    toArray((scope || document).querySelectorAll('[data-sma-cslider]')).forEach(initSlider);
  }

  /* Arrows + dots — delegated so async-rendered grids work with no re-binding. */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var arrow = event.target.closest('.sma-cslider__arrow');
    if (arrow) {
      event.preventDefault();
      event.stopPropagation();
      var arrowRoot = arrow.closest('[data-sma-cslider]');
      if (!arrowRoot) return;
      var step = arrow.classList.contains('sma-cslider__arrow--prev') ? -1 : 1;
      goToSlide(arrowRoot, currentIndex(sliderParts(arrowRoot)) + step);
      return;
    }

    var dot = event.target.closest('.sma-cslider__dot');
    if (dot) {
      event.preventDefault();
      event.stopPropagation();
      var dotRoot = dot.closest('[data-sma-cslider]');
      if (!dotRoot) return;
      goToSlide(dotRoot, parseInt(dot.getAttribute('data-sma-index'), 10) || 0);
    }
  });

  /* ------------------------------------------------------------------ */
  /* 2. Model-photo toggle                                               */
  /* ------------------------------------------------------------------ */

  function toggleScope(root) {
    var selector = root.getAttribute('data-sma-scope');
    if (selector) {
      var byId = document.querySelector(selector);
      if (byId) return byId;
    }
    return root.closest('.shopify-section') || document;
  }

  /* --- cards --- */

  function cardMatchIndex(slider, keywords) {
    var slides = toArray(slider.querySelectorAll('.sma-cslider__slide'));
    for (var i = 0; i < slides.length; i++) {
      if (altMatches(slides[i].getAttribute('data-sma-alt'), keywords)) return i;
    }
    return -1;
  }

  function detectCards(scope, keywords) {
    var sliders = toArray(scope.querySelectorAll('[data-sma-cslider]'));
    for (var i = 0; i < sliders.length; i++) {
      if (cardMatchIndex(sliders[i], keywords) > -1) return true;
    }
    return false;
  }

  /* State markers keep repeat calls inert, so the MutationObserver below can
     never bounce off its own DOM writes. */
  function applyToCards(scope, keywords, on) {
    var wanted = on ? 'on' : 'off';
    toArray(scope.querySelectorAll('[data-sma-cslider]')).forEach(function (slider) {
      initSlider(slider);
      if (slider.dataset.smaModelState === wanted) return;
      slider.dataset.smaModelState = wanted;

      if (on) {
        var index = cardMatchIndex(slider, keywords);
        if (index > -1) goToSlide(slider, index, 'instant');
      } else {
        goToSlide(slider, 0, 'instant');
      }
    });
  }

  /* --- product page gallery --- */

  function galleryItemAlt(item) {
    var img = item.querySelector('img');
    return img ? img.getAttribute('alt') : '';
  }

  function galleryLists(scope) {
    var galleries = toArray(scope.querySelectorAll('media-gallery'));
    /* The toggle is rendered inside <media-gallery>, so the scope can be the
       gallery itself rather than an ancestor of it. */
    if (scope.matches && scope.matches('media-gallery')) galleries.unshift(scope);

    return galleries
      .map(function (gallery) {
        return gallery.querySelector('.product__media-list');
      })
      .filter(Boolean);
  }

  function galleryMatch(list, keywords) {
    var order = list.smaOriginalOrder || toArray(list.children);
    for (var i = 0; i < order.length; i++) {
      if (altMatches(galleryItemAlt(order[i]), keywords)) return order[i];
    }
    return null;
  }

  function detectGalleries(scope, keywords) {
    var lists = galleryLists(scope);
    for (var i = 0; i < lists.length; i++) {
      if (galleryMatch(lists[i], keywords)) return true;
    }
    return false;
  }

  function applyToGalleries(scope, keywords, on) {
    var wanted = on ? 'on' : 'off';
    galleryLists(scope).forEach(function (list) {
      if (!list.smaOriginalOrder) list.smaOriginalOrder = toArray(list.children);
      if (list.smaModelState === wanted) return;
      list.smaModelState = wanted;

      if (on) {
        var target = galleryMatch(list, keywords);
        if (!target || list.firstElementChild === target) return;
        list.insertBefore(target, list.firstElementChild);
      } else {
        if (list.firstElementChild === list.smaOriginalOrder[0]) return;
        list.smaOriginalOrder.forEach(function (item) {
          list.appendChild(item);
        });
      }

      if (typeof list.scrollTo === 'function') {
        list.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        list.scrollLeft = 0;
      }
    });
  }

  /* --- state --- */

  function applyToggle(root, on) {
    var keywords = keywordsOf(root);
    if (!keywords.length) return;

    var scope = toggleScope(root);
    if (root.getAttribute('data-sma-target') === 'gallery') {
      applyToGalleries(scope, keywords, on);
    } else {
      applyToCards(scope, keywords, on);
    }
  }

  function toggleIsOn(root) {
    var button = root.querySelector('.sma-modeltoggle__btn');
    return !!button && button.getAttribute('aria-checked') === 'true';
  }

  function setToggle(root, on) {
    var button = root.querySelector('.sma-modeltoggle__btn');
    if (button) button.setAttribute('aria-checked', on ? 'true' : 'false');
    root.setAttribute('data-sma-on', on ? 'true' : 'false');
    applyToggle(root, on);
  }

  /* Hide the switch when nothing in its scope has a matching image, and bring
     freshly rendered cards in line with the current state. */
  function refreshToggle(root) {
    var keywords = keywordsOf(root);
    if (!keywords.length) {
      root.hidden = true;
      return;
    }

    var scope = toggleScope(root);
    var isGallery = root.getAttribute('data-sma-target') === 'gallery';
    var found = isGallery ? detectGalleries(scope, keywords) : detectCards(scope, keywords);

    root.hidden = !found;
    if (found) applyToggle(root, toggleIsOn(root));
  }

  function refreshAllToggles(scope) {
    toArray((scope || document).querySelectorAll('[data-sma-modeltoggle]')).forEach(refreshToggle);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var button = event.target.closest('.sma-modeltoggle__btn');
    if (!button) return;
    event.preventDefault();
    var root = button.closest('[data-sma-modeltoggle]');
    if (!root) return;
    setToggle(root, !toggleIsOn(root));
  });

  /* ------------------------------------------------------------------ */
  /* 3. Collection tabs (featured collection)                            */
  /* ------------------------------------------------------------------ */

  function selectTab(tab) {
    var bar = tab.closest('[data-sma-tabs]');
    if (!bar) return;
    var scopeSelector = bar.getAttribute('data-sma-tabs');
    var scope = (scopeSelector && document.querySelector(scopeSelector)) || bar.closest('.shopify-section') || document;
    var targetId = tab.getAttribute('data-sma-tab-target');

    toArray(bar.querySelectorAll('.sma-ctab')).forEach(function (other) {
      var active = other === tab;
      other.classList.toggle('sma-ctab--active', active);
      other.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    toArray(scope.querySelectorAll('.sma-tabpanel')).forEach(function (panel) {
      panel.hidden = panel.id !== targetId;
    });

    /* A hidden panel has zero width, so its sliders could not scroll to the
       right slide. Clear their state so the toggle re-applies now they show. */
    var shown = scope.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(targetId) : targetId));
    if (shown) {
      toArray(shown.querySelectorAll('[data-sma-cslider]')).forEach(function (slider) {
        delete slider.dataset.smaModelState;
      });
    }

    /* slider-component measures on resize — nudge it now the panel is visible. */
    window.dispatchEvent(new Event('resize'));
    initAllSliders(scope);
    refreshAllToggles(scope);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var tab = event.target.closest('.sma-ctab');
    if (!tab) return;
    event.preventDefault();
    selectTab(tab);
  });

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function boot() {
    initAllSliders(document);
    refreshAllToggles(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllSliders(event.target || document);
    refreshAllToggles(event.target || document);
  });

  /* Grids re-render asynchronously (facets, product recommendations, variant
     changes) — re-scan after the DOM settles. */
  var rescanTimer = null;
  var observer = new MutationObserver(function () {
    window.clearTimeout(rescanTimer);
    rescanTimer = window.setTimeout(boot, 200);
  });

  function observe() {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
